import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";
import { apiFunction } from "../utils";

/**
 * Pending Device installation API (storage roles).
 * Issue: bayineras/BayinerIOT-Workspace-#16
 *
 * Endpoint'ler server tarafında /api/pending-devices altında, sadece
 * storage rollerine açık (admin, management, service, playmaker).
 */
export const pendingDevicesApi = createApi({
  reducerPath: "pendingDevices",
  tagTypes: ["PendingDevice"],
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  keepUnusedDataFor: 60,
  endpoints(builder) {
    return {
      // Listele — mode/status filtresi opsiyonel
      getPendingDevices: builder.query({
        query: ({ token, mode, status }) => {
          const qs = new URLSearchParams();
          if (mode) qs.append("mode", mode);
          if (status) qs.append("status", status);
          const url = `/api/pending-devices${qs.toString() ? `?${qs}` : ""}`;
          return {
            url,
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          };
        },
        transformResponse: (response) => response?.data?.items || [],
        providesTags: (result) =>
          result
            ? [
                { type: "PendingDevice", id: "LIST" },
                ...result.map((item) => ({
                  type: "PendingDevice",
                  id: item._id,
                })),
              ]
            : [{ type: "PendingDevice", id: "LIST" }],
      }),

      // Yeni kurulum onayla
      assignNewInstall: builder.mutation({
        query: ({ token, id, ...body }) =>
          apiFunction(
            { token, ...body },
            `/api/pending-devices/${id}/assign`,
            "POST"
          ),
        invalidatesTags: (_res, _err, arg) => [
          { type: "PendingDevice", id: "LIST" },
          { type: "PendingDevice", id: arg.id },
        ],
      }),

      // Ekran değişimi onayla
      replaceScreen: builder.mutation({
        query: ({ token, id, deviceId }) =>
          apiFunction(
            { token, deviceId },
            `/api/pending-devices/${id}/replace-screen`,
            "POST"
          ),
        invalidatesTags: (_res, _err, arg) => [
          { type: "PendingDevice", id: "LIST" },
          { type: "PendingDevice", id: arg.id },
        ],
      }),

      // Migration onayla — eski cihaz yeni MQTT cred almak için pending'e
      // düşmüş, admin onaylar, server EMQX'te user yaratır + cred yollar
      approveMigration: builder.mutation({
        query: ({ token, id }) =>
          apiFunction(
            { token },
            `/api/pending-devices/${id}/approve-migration`,
            "POST"
          ),
        invalidatesTags: (_res, _err, arg) => [
          { type: "PendingDevice", id: "LIST" },
          { type: "PendingDevice", id: arg.id },
        ],
      }),

      // Reddet — Yeni Kurulum / Ekran Değişimi / Yükseltme isteğini
      // pending → rejected'a çevir. Cihaz /install/check ile sebebi görür.
      rejectPendingDevice: builder.mutation({
        query: ({ token, id, reason }) =>
          apiFunction(
            { token, reason },
            `/api/pending-devices/${id}/reject`,
            "POST"
          ),
        invalidatesTags: (_res, _err, arg) => [
          { type: "PendingDevice", id: "LIST" },
          { type: "PendingDevice", id: arg.id },
        ],
      }),
    };
  },
});

export const {
  useGetPendingDevicesQuery,
  useAssignNewInstallMutation,
  useReplaceScreenMutation,
  useApproveMigrationMutation,
  useRejectPendingDeviceMutation,
} = pendingDevicesApi;
